import { ChangeEvent, FocusEvent, useState, forwardRef, useRef, AriaAttributes } from 'react';
import classNames from 'classnames';
import { Icon } from '@components/icon';
import { Body } from '@components/text';
import './Radio.scss';

export interface RadioProps extends AriaAttributes {
  /** id used to connect label to input element for screen reader */
  id: string;
  /** name used to group radio buttons together */
  name: string;
  /** value of radio button */
  value: string;
  /** disable the radio button */
  disabled?: boolean;
  /** callback function when radio button is changed */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** callback function when radio button is focused */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /** callback function when radio button is blurred */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** callback function when key is pressed down */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** callback function when mouse is clicked down */
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** size of label, default large */
  size?: 'small' | 'large';
  /** style of radio button */
  variant?: 'dot' | 'checkmark';
  /** first line of text next to button */
  label?: string;
  /** second line of text next to button */
  description?: string;
  /** checked state of radio button */
  checked?: boolean;
  /** ref to input element */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

/**
 * Radio Base component
 * an <input type="radio" /> element is underneath to supports accessibility
 * @param id - used to connect label to input element for screen reader
 * @param name - used to group radio buttons together
 * @param value - value of radio button
 * @param disabled - disable the radio button
 * @param onChange - callback function when radio button is changed
 * @param onFocus - callback function when radio button is focused
 * @param onBlur - callback function when radio button is blurred
 * @param onKeyDown - callback function when key is pressed down
 * @param onMouseDown - callback function when mouse is clicked down
 * @param size - size of label, default large
 * @param variant - style of radio button
 * @param label - first line of text next to button
 * @param description - second line of text next to button
 * @param checked - checked state of radio button
 * @param inputRef - ref to input element
 * @constructor
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      id,
      name,
      value,
      checked,
      disabled,
      onChange,
      onFocus,
      onBlur,
      onMouseDown,
      onKeyDown,
      size = 'large',
      label,
      description,
      variant = 'checkmark',
      inputRef,
      ...rest
    },
    ref,
  ) => {
    const radioRef = useRef<HTMLInputElement | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      if (disabled) return;
      setIsFocused(true);
      radioRef?.current?.click();
      if (onFocus) onFocus(e);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      if (disabled) return;
      setIsFocused(false);
      if (onBlur) onBlur(e);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (onChange) onChange(e);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      e.preventDefault();
      radioRef?.current?.focus();
      if (onMouseDown) onMouseDown(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        radioRef?.current?.focus();
      }
      if (onKeyDown) onKeyDown(e);
    };

    const initRefs = (element: HTMLInputElement) => {
      radioRef.current = element;
      if (inputRef) inputRef.current = element;

      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else {
          ref.current = element;
        }
      }
    };

    return (
      <div
        onMouseDown={handleMouseDown}
        className={classNames('vds-radio', {
          'vds-radio--checked': checked,
          'vds-radio--focused': isFocused,
          'vds-radio--disabled': disabled,
          [`vds-radio--${size}`]: size,
        })}>
        <span className="vds-radio__icon-wrapper">
          <Icon
            className="vds-radio__icon"
            name={checked ? `radio-${variant}` : 'checkbox-unselected-circle'}
          />
        </span>

        <input
          id={id}
          type="radio"
          className="vds-radio__input"
          disabled={disabled}
          name={name}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          checked={checked}
          ref={initRefs}
          role="radio"
          aria-checked={rest['aria-checked'] || checked}
          aria-label={rest['aria-label'] || label}
          aria-labelledby={rest['aria-labelledby'] || `${id}-label`}
          {...rest}
        />

        <label id={`${id}-label`} htmlFor={id} className="vds-radio__label">
          {label && (
            <Body
              variant="body-02"
              color={checked ? 'primary' : 'tertiary'}
              weight={checked ? 'semibold' : 'regular'}>
              {label}
            </Body>
          )}
          {description && (
            <Body
              variant="body-02"
              color={checked ? 'primary' : 'tertiary'}
              weight={checked ? 'semibold' : 'regular'}>
              {description}
            </Body>
          )}
        </label>
      </div>
    );
  },
);
